import React, { useState } from 'react';
import { Share2, Facebook, Twitter, MessageCircle, Link, Check, ExternalLink, Heart, X } from 'lucide-react';
import { Business, Review } from '../types';

interface SocialShareProps {
  business?: Business;
  review?: Review;
  type: 'business' | 'review';
  onClose?: () => void;
}

const SocialShare: React.FC<SocialShareProps> = ({ business, review, type, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [sharedPlatforms, setSharedPlatforms] = useState<string[]>([]);
  const [isSharing, setIsSharing] = useState<string | null>(null);

  const generateShareText = () => {
    if (type === 'business' && business) {
      return `Check out ${business.name}! ${business.description || 'Great business with positive reviews.'} #Piezit #PositiveReviews`;
    } else if (type === 'review' && review && business) {
      return `Just shared a positive review for ${business.name} on Piezit! ${review.title} #Piezit #Review`;
    }
    return 'Discover amazing businesses on Piezit! #Piezit';
  };

  const generateShareUrl = () => {
    const baseUrl = window.location.origin;
    if (type === 'business' && business) {
      return `${baseUrl}/business/${business.id}`;
    } else if (type === 'review' && review) {
      return `${baseUrl}/review/${review.id}`;
    }
    return baseUrl;
  };

  const shareText = generateShareText();
  const shareUrl = generateShareUrl();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const shareOnPlatform = async (platform: string) => {
    setIsSharing(platform);
    
    let url = '';
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
        break;
      default:
        setIsSharing(null);
        return;
    }

    // Open sharing window
    const shareWindow = window.open(url, '_blank', 'width=600,height=400');
    
    // Simulate successful share after a delay
    setTimeout(() => {
      setIsSharing(null);
      if (!sharedPlatforms.includes(platform)) {
        setSharedPlatforms(prev => [...prev, platform]);
      }
      
      // Track sharing analytics (in a real app)
      console.log(`Shared ${type} on ${platform}:`, { business: business?.name, review: review?.title });
    }, 2000);

    // Focus back to main window when share window closes
    const checkClosed = setInterval(() => {
      if (shareWindow?.closed) {
        clearInterval(checkClosed);
        setIsSharing(null);
      }
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Share {type === 'business' ? 'Business' : 'Review'}
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Preview */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Share2 className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 truncate">
                {business?.name || 'Piezit Business'}
              </h4>
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {shareText}
              </p>
              <p className="text-xs text-gray-500 mt-2 truncate">
                {shareUrl}
              </p>
            </div>
          </div>
        </div>

        {/* Share Options */}
        <div className="space-y-3">
          <button
            onClick={() => shareOnPlatform('facebook')}
            disabled={isSharing === 'facebook'}
            className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                {isSharing === 'facebook' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Facebook className="w-4 h-4 text-white" />
                )}
              </div>
              <span className="font-medium text-gray-900">Share on Facebook</span>
            </div>
            {sharedPlatforms.includes('facebook') && (
              <div className="flex items-center space-x-1 text-green-600">
                <Check className="w-4 h-4" />
                <span className="text-sm">Shared</span>
              </div>
            )}
          </button>

          <button
            onClick={() => shareOnPlatform('twitter')}
            disabled={isSharing === 'twitter'}
            className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-400 rounded-lg flex items-center justify-center">
                {isSharing === 'twitter' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Twitter className="w-4 h-4 text-white" />
                )}
              </div>
              <span className="font-medium text-gray-900">Share on Twitter</span>
            </div>
            {sharedPlatforms.includes('twitter') && (
              <div className="flex items-center space-x-1 text-green-600">
                <Check className="w-4 h-4" />
                <span className="text-sm">Shared</span>
              </div>
            )}
          </button>

          <button
            onClick={() => shareOnPlatform('whatsapp')}
            disabled={isSharing === 'whatsapp'}
            className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                {isSharing === 'whatsapp' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <MessageCircle className="w-4 h-4 text-white" />
                )}
              </div>
              <span className="font-medium text-gray-900">Share on WhatsApp</span>
            </div>
            {sharedPlatforms.includes('whatsapp') && (
              <div className="flex items-center space-x-1 text-green-600">
                <Check className="w-4 h-4" />
                <span className="text-sm">Shared</span>
              </div>
            )}
          </button>

          <button
            onClick={copyToClipboard}
            className="w-full flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center">
              {copied ? (
                <Check className="w-4 h-4 text-white" />
              ) : (
                <Link className="w-4 h-4 text-white" />
              )}
            </div>
            <span className="font-medium text-gray-900">
              {copied ? 'Copied to clipboard!' : 'Copy link'}
            </span>
          </button>
        </div>

        {/* Success Feedback */}
        {sharedPlatforms.length > 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Heart className="w-4 h-4 text-green-600 fill-current" />
              <span className="text-sm font-medium text-green-900">
                Thanks for spreading positivity!
              </span>
            </div>
            <p className="text-xs text-green-800 mt-1">
              You've shared on {sharedPlatforms.length} platform{sharedPlatforms.length > 1 ? 's' : ''}. 
              Your positive words help businesses and customers connect!
            </p>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Help spread positivity by sharing great businesses!
          </p>
          {sharedPlatforms.length > 0 && (
            <div className="flex items-center justify-center space-x-1 mt-2">
              <ExternalLink className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-400">
                Shared links lead back to Piezit for more details
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialShare;
